import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
} from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { useLocation } from 'react-router-dom';
import { get } from '../utils/api';

import { getKotilingaCategorySlugsCsv } from '../utils/kotilingaScope';
const FETCH_LIMIT = 100;
const ROWS_PER_PAGE = 10;
// Distinct color families per period (blue, green, red, orange, purple, teal)
const COMPARE_PERIOD_COLORS = ['#1565c0', '#2e7d32', '#c62828', '#ef6c00', '#6a1b9a', '#00838f'];

function formatCurrency(n) {
  const num = parseFloat(n);
  if (isNaN(num)) return '$0.00';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(num);
}

function getPeriodBounds(periodType, periodValue) {
  if (periodType === 'year') {
    const y = parseInt(periodValue, 10);
    return { start: `${y}-01-01T00:00:00.000Z`, end: `${y}-12-31T23:59:59.999Z`, label: String(y) };
  }
  if (periodType === 'quarter') {
    const [q, y] = periodValue.split('-').map((s) => s.trim());
    const year = parseInt(y, 10);
    const qNum = parseInt(q.replace('Q', ''), 10);
    const startMonth = (qNum - 1) * 3 + 1;
    const endMonth = qNum * 3;
    const start = `${year}-${String(startMonth).padStart(2, '0')}-01T00:00:00.000Z`;
    const lastDay = new Date(year, endMonth, 0).getDate();
    const end = `${year}-${String(endMonth).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}T23:59:59.999Z`;
    const label = `Q${qNum} ${y}`;
    return { start, end, label };
  }
  if (periodType === 'month') {
    const [mm, y] = periodValue.split('-').map((s) => s.trim());
    const year = parseInt(y, 10);
    const month = parseInt(mm, 10);
    const lastDay = new Date(year, month, 0).getDate();
    const start = `${year}-${String(month).padStart(2, '0')}-01T00:00:00.000Z`;
    const end = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}T23:59:59.999Z`;
    const label = new Date(year, month - 1, 1).toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
    return { start, end, label };
  }
  return { start: null, end: null, label: periodValue };
}

export default function AnalyticsCompareView() {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const viewParam = searchParams.get('view');
  const viewMode =
    viewParam === 'kotilinga' ? 'kotilinga' : viewParam === 'all' ? 'all' : 'temple';

  const [dateRangeBounds, setDateRangeBounds] = useState({ minDate: null, maxDate: null });
  const [periodType, setPeriodType] = useState('year');
  const [availablePeriods, setAvailablePeriods] = useState([]);
  const [selectedPeriodValues, setSelectedPeriodValues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingCompare, setLoadingCompare] = useState(false);
  const [error, setError] = useState('');
  const [productByPeriod, setProductByPeriod] = useState([]);
  const [categoryByPeriod, setCategoryByPeriod] = useState([]);
  const [productPage, setProductPage] = useState(0);
  const [categoryPage, setCategoryPage] = useState(0);

  const buildParams = useCallback(
    (startIso, endIso) => {
      const params = { start: startIso, end: endIso, limit: FETCH_LIMIT };
      const scopeSlugs = getKotilingaCategorySlugsCsv();
      if (viewMode === 'temple') params.exclude_parent_category_slugs = scopeSlugs;
      else if (viewMode === 'kotilinga') params.parent_category_slugs = scopeSlugs;
      return params;
    },
    [viewMode]
  );

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await get('/ldb/analytics/date-range');
        if (cancelled) return;
        const min = r?.minDate ? new Date(r.minDate) : null;
        const max = r?.maxDate ? new Date(r.maxDate) : null;
        setDateRangeBounds({ minDate: min, maxDate: max });
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Failed to load date range');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const periodsForType = useMemo(() => {
    const { minDate, maxDate } = dateRangeBounds;
    if (!minDate || !maxDate) return [];
    const minY = minDate.getFullYear();
    const maxY = maxDate.getFullYear();
    if (periodType === 'year') {
      return Array.from({ length: maxY - minY + 1 }, (_, i) => ({ value: String(minY + i), label: String(minY + i) }));
    }
    if (periodType === 'quarter') {
      const list = [];
      for (let y = minY; y <= maxY; y++) {
        for (let q = 1; q <= 4; q++) {
          const start = new Date(y, (q - 1) * 3, 1);
          const end = new Date(y, q * 3, 0);
          if (start >= minDate && end <= maxDate) list.push({ value: `Q${q}-${y}`, label: `Q${q} ${y}` });
        }
      }
      return list.reverse();
    }
    if (periodType === 'month') {
      const list = [];
      const cur = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
      const end = new Date(maxDate.getFullYear(), maxDate.getMonth(), 1);
      while (cur <= end) {
        const y = cur.getFullYear();
        const m = cur.getMonth() + 1;
        list.push({
          value: `${m}-${y}`,
          label: cur.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
        });
        cur.setMonth(cur.getMonth() + 1);
      }
      return list.reverse();
    }
    return [];
  }, [dateRangeBounds, periodType]);

  useEffect(() => {
    setAvailablePeriods(periodsForType);
    setSelectedPeriodValues([]);
  }, [periodsForType]);

  const loadCompare = useCallback(async () => {
    if (selectedPeriodValues.length === 0) {
      setProductByPeriod([]);
      setCategoryByPeriod([]);
      setProductPage(0);
      setCategoryPage(0);
      return;
    }
    setLoadingCompare(true);
    setError('');
    try {
      const boundsList = selectedPeriodValues.map((v) => getPeriodBounds(periodType, v));
      const paramsList = boundsList.map(({ start, end }) => buildParams(start, end));

      const productPromises = paramsList.map((params) => get('/ldb/analytics/product-split', params));
      const categoryPromises = paramsList.map((params) => get('/ldb/analytics/category-split', params));

      const productResults = await Promise.all(productPromises);
      const categoryResults = await Promise.all(categoryPromises);

      const productMap = {};
      boundsList.forEach(({ label }, i) => {
        const data = Array.isArray(productResults[i]?.data) ? productResults[i].data : [];
        data.forEach((row) => {
          const id = row.product_id;
          const name = row.product_name || `Product ${id}`;
          if (!productMap[id]) productMap[id] = { product_id: id, name: name.slice(0, 40) };
          productMap[id][label] = parseFloat(row.amount) || 0;
        });
      });
      const productMerged = Object.values(productMap).sort((a, b) => {
        const aSum = boundsList.reduce((s, { label }) => s + (a[label] || 0), 0);
        const bSum = boundsList.reduce((s, { label }) => s + (b[label] || 0), 0);
        return bSum - aSum;
      });

      const categoryMap = {};
      boundsList.forEach(({ label }, i) => {
        const data = Array.isArray(categoryResults[i]?.data) ? categoryResults[i].data : [];
        data.forEach((row) => {
          const id = row.category_id;
          const name = row.category_name || `Category ${id}`;
          if (!categoryMap[id]) categoryMap[id] = { category_id: id, name: name.slice(0, 40) };
          categoryMap[id][label] = parseFloat(row.amount) || 0;
        });
      });
      const categoryMerged = Object.values(categoryMap).sort((a, b) => {
        const aSum = boundsList.reduce((s, { label }) => s + (a[label] || 0), 0);
        const bSum = boundsList.reduce((s, { label }) => s + (b[label] || 0), 0);
        return bSum - aSum;
      });

      setProductByPeriod(productMerged);
      setCategoryByPeriod(categoryMerged);
      setProductPage(0);
      setCategoryPage(0);
    } catch (e) {
      setError(e?.message || 'Failed to load comparison');
      setProductByPeriod([]);
      setCategoryByPeriod([]);
      setProductPage(0);
      setCategoryPage(0);
    } finally {
      setLoadingCompare(false);
    }
  }, [selectedPeriodValues, periodType, buildParams]);

  useEffect(() => {
    if (selectedPeriodValues.length > 0) loadCompare();
    else {
      setProductByPeriod([]);
      setCategoryByPeriod([]);
    }
  }, [selectedPeriodValues, periodType, viewMode, location.search, loadCompare]);

  const periodLabels = useMemo(() => {
    return selectedPeriodValues.map((v) => getPeriodBounds(periodType, v).label);
  }, [selectedPeriodValues, periodType]);

  const productChartData = useMemo(() => {
    return productByPeriod.map((row) => {
      const out = { name: row.name };
      periodLabels.forEach((label) => (out[label] = row[label] || 0));
      return out;
    });
  }, [productByPeriod, periodLabels]);

  const categoryChartData = useMemo(() => {
    return categoryByPeriod.map((row) => {
      const out = { name: row.name };
      periodLabels.forEach((label) => (out[label] = row[label] || 0));
      return out;
    });
  }, [categoryByPeriod, periodLabels]);

  const sortPeriodValues = useCallback(
    (values) => {
      return [...values].sort((a, b) => {
        const sa = getPeriodBounds(periodType, a).start || '';
        const sb = getPeriodBounds(periodType, b).start || '';
        return sa.localeCompare(sb);
      });
    },
    [periodType]
  );

  const handlePeriodToggle = (value) => {
    setSelectedPeriodValues((prev) => {
      const next = prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value];
      return sortPeriodValues(next);
    });
  };

  const selectAllPeriods = () => {
    setSelectedPeriodValues(sortPeriodValues(availablePeriods.map((p) => p.value)));
  };

  const clearPeriods = () => setSelectedPeriodValues([]);

  return (
    <>
      {loading ? (
        <Typography color="text.secondary">Loading…</Typography>
      ) : (
        <>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start" flexWrap="wrap">
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Period type</InputLabel>
                <Select
                  label="Period type"
                  value={periodType}
                  onChange={(e) => setPeriodType(e.target.value)}
                >
                  <MenuItem value="year">Year</MenuItem>
                  <MenuItem value="quarter">Quarter</MenuItem>
                  <MenuItem value="month">Month</MenuItem>
                </Select>
              </FormControl>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Select periods to compare
                </Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  <Button size="small" onClick={selectAllPeriods}>All</Button>
                  <Button size="small" onClick={clearPeriods}>Clear</Button>
                </Stack>
                <FormGroup row sx={{ mt: 0.5, maxHeight: 120, overflow: 'auto' }}>
                  {availablePeriods.map((p) => (
                    <FormControlLabel
                      key={p.value}
                      control={
                        <Checkbox
                          checked={selectedPeriodValues.includes(p.value)}
                          onChange={() => handlePeriodToggle(p.value)}
                          size="small"
                        />
                      }
                      label={p.label}
                    />
                  ))}
                </FormGroup>
              </Box>
            </Stack>
          </Paper>

          {error && (
            <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>
          )}

          {loadingCompare && (
            <Typography color="text.secondary" sx={{ mb: 2 }}>Loading comparison…</Typography>
          )}

          {!loadingCompare && selectedPeriodValues.length > 0 && (
            <>
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" gutterBottom>Revenue by product</Typography>
                <Box sx={{ display: 'flex', width: '100%', gap: 2, flexDirection: 'column' }}>
                  <Box sx={{ width: '100%', minWidth: 0 }}>
                    {productChartData.length > 0 ? (
                      <ResponsiveContainer width="100%" height={400}>
                        <BarChart data={productChartData} margin={{ top: 8, right: 8, left: 8, bottom: 80 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" angle={-35} textAnchor="end" height={80} tick={{ fontSize: 10 }} />
                          <YAxis tickFormatter={(v) => `$${v >= 1000 ? (v / 1000) + 'k' : v}`} />
                          <Tooltip formatter={(v) => [formatCurrency(v), 'Revenue']} />
                          <Legend />
                          {periodLabels.map((label, i) => (
                            <Bar
                              key={label}
                              dataKey={label}
                              name={label}
                              fill={COMPARE_PERIOD_COLORS[i % COMPARE_PERIOD_COLORS.length]}
                              radius={[0, 2, 2, 0]}
                            />
                          ))}
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <Typography variant="body2" color="text.secondary">No product data for selected periods.</Typography>
                    )}
                  </Box>
                  <Box sx={{ width: '100%', minWidth: 0, maxHeight: 440, overflow: 'auto' }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.100' }}>
                          <TableCell>Product</TableCell>
                          {periodLabels.map((l) => (
                            <TableCell key={l} align="right">{l}</TableCell>
                          ))}
                          {periodLabels.length >= 2 && (
                            <TableCell align="right">% change</TableCell>
                          )}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {productByPeriod
                          .slice(productPage * ROWS_PER_PAGE, productPage * ROWS_PER_PAGE + ROWS_PER_PAGE)
                          .map((row) => {
                            const first = periodLabels[0] ? (row[periodLabels[0]] || 0) : 0;
                            const last = periodLabels.length ? (row[periodLabels[periodLabels.length - 1]] || 0) : 0;
                            const pct = first > 0 ? (((last - first) / first) * 100).toFixed(1) : (last > 0 ? '—' : '—');
                            return (
                              <TableRow key={row.product_id}>
                                <TableCell>{row.name}</TableCell>
                                {periodLabels.map((l) => (
                                  <TableCell key={l} align="right">{formatCurrency(row[l] || 0)}</TableCell>
                                ))}
                                {periodLabels.length >= 2 && (
                                  <TableCell align="right">{pct !== '—' ? `${pct}%` : pct}</TableCell>
                                )}
                              </TableRow>
                            );
                          })}
                      </TableBody>
                    </Table>
                  </Box>
                  {productByPeriod.length > ROWS_PER_PAGE && (
                    <TablePagination
                      component="div"
                      count={productByPeriod.length}
                      rowsPerPage={ROWS_PER_PAGE}
                      rowsPerPageOptions={[ROWS_PER_PAGE]}
                      page={productPage}
                      onPageChange={(_, p) => setProductPage(p)}
                      labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
                    />
                  )}
                </Box>
              </Paper>

              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" gutterBottom>Revenue by category</Typography>
                <Box sx={{ display: 'flex', width: '100%', gap: 2, flexDirection: 'column' }}>
                  <Box sx={{ width: '100%', minWidth: 0 }}>
                    {categoryChartData.length > 0 ? (
                      <ResponsiveContainer width="100%" height={400}>
                        <BarChart data={categoryChartData} margin={{ top: 8, right: 8, left: 8, bottom: 80 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" angle={-35} textAnchor="end" height={80} tick={{ fontSize: 10 }} />
                          <YAxis tickFormatter={(v) => `$${v >= 1000 ? (v / 1000) + 'k' : v}`} />
                          <Tooltip formatter={(v) => [formatCurrency(v), 'Revenue']} />
                          <Legend />
                          {periodLabels.map((label, i) => (
                            <Bar
                              key={label}
                              dataKey={label}
                              name={label}
                              fill={COMPARE_PERIOD_COLORS[i % COMPARE_PERIOD_COLORS.length]}
                              radius={[0, 2, 2, 0]}
                            />
                          ))}
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <Typography variant="body2" color="text.secondary">No category data for selected periods.</Typography>
                    )}
                  </Box>
                  <Box sx={{ width: '100%', minWidth: 0, maxHeight: 440, overflow: 'auto' }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.100' }}>
                          <TableCell>Category</TableCell>
                          {periodLabels.map((l) => (
                            <TableCell key={l} align="right">{l}</TableCell>
                          ))}
                          {periodLabels.length >= 2 && (
                            <TableCell align="right">% change</TableCell>
                          )}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {categoryByPeriod
                          .slice(categoryPage * ROWS_PER_PAGE, categoryPage * ROWS_PER_PAGE + ROWS_PER_PAGE)
                          .map((row) => {
                            const first = periodLabels[0] ? (row[periodLabels[0]] || 0) : 0;
                            const last = periodLabels.length ? (row[periodLabels[periodLabels.length - 1]] || 0) : 0;
                            const pct = first > 0 ? (((last - first) / first) * 100).toFixed(1) : (last > 0 ? '—' : '—');
                            return (
                              <TableRow key={row.category_id}>
                                <TableCell>{row.name}</TableCell>
                                {periodLabels.map((l) => (
                                  <TableCell key={l} align="right">{formatCurrency(row[l] || 0)}</TableCell>
                                ))}
                                {periodLabels.length >= 2 && (
                                  <TableCell align="right">{pct !== '—' ? `${pct}%` : pct}</TableCell>
                                )}
                              </TableRow>
                            );
                          })}
                      </TableBody>
                    </Table>
                  </Box>
                  {categoryByPeriod.length > ROWS_PER_PAGE && (
                    <TablePagination
                      component="div"
                      count={categoryByPeriod.length}
                      rowsPerPage={ROWS_PER_PAGE}
                      rowsPerPageOptions={[ROWS_PER_PAGE]}
                      page={categoryPage}
                      onPageChange={(_, p) => setCategoryPage(p)}
                      labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
                    />
                  )}
                </Box>
              </Paper>
            </>
          )}

          {!loadingCompare && selectedPeriodValues.length === 0 && (
            <Typography color="text.secondary">Select one or more periods above to compare revenue by product and category.</Typography>
          )}
        </>
      )}
    </>
  );
}
